import type { AriDealLabelId, PipelineColumnConfig, PipelinePreset, DealType } from "@/types/deal.types"

export const PIPELINE_COLUMNS: PipelineColumnConfig[] = [
  { id: "lead", title: "Lead", color: "bg-orange-500" },
  { id: "in_progress", title: "In Progress", color: "bg-orange-500" },
  { id: "signing", title: "Signing", color: "bg-orange-500" },
  { id: "delivery", title: "Delivery", color: "bg-orange-500" },
  { id: "complete", title: "Complete", color: "bg-orange-500" },
]

/** Ari demo pipeline — maps from: ari/public/email-client.html PIPELINE_COLS */
export const ARI_PIPELINE_COLUMNS: PipelineColumnConfig[] = [
  { id: "lead", title: "Lead", color: "bg-[#4d6af5]", pillTextClass: "text-white" },
  { id: "in_progress", title: "In Progress", color: "bg-[#8b5cf6]", pillTextClass: "text-white" },
  { id: "won", title: "Won", color: "bg-[#22c55e]", pillTextClass: "text-white" },
  { id: "lost", title: "Lost", color: "bg-[#ef4444]", pillTextClass: "text-white" },
  { id: "closed", title: "Closed", color: "bg-[#f59e0b]", pillTextClass: "text-white" },
]

export const PIPELINE_PRESETS: PipelinePreset[] = [
  { id: "default", name: "Default", columns: ["lead", "in_progress", "signing", "delivery", "complete"] },
  { id: "active-only", name: "Active Only", columns: ["lead", "in_progress", "signing"] },
  { id: "outcomes", name: "Outcomes", columns: ["delivery", "complete"] },
]

export const ARI_PIPELINE_PRESETS: PipelinePreset[] = [
  {
    id: "ari-default",
    name: "All deals",
    columns: ["lead", "in_progress", "won", "lost", "closed"],
  },
]

export const ARI_DEAL_LABEL_COLORS: Record<AriDealLabelId, { name: string; className: string }> = {
  "music-deal": { name: "Music Deal", className: "bg-[#8b5cf6]/20 text-[#a78bfa]" },
  "brand-deal": { name: "Brand Deal", className: "bg-[#f59e0b]/20 text-[#fbbf24]" },
  licensing: { name: "Licensing Agreement", className: "bg-[#14b8a6]/20 text-[#2dd4bf]" },
  "sports-deal": { name: "Sports Deal", className: "bg-[#22c55e]/20 text-[#4ade80]" },
  publishing: { name: "Publishing Deal", className: "bg-[#ec4899]/20 text-[#f472b6]" },
  touring: { name: "Touring Rights", className: "bg-[#f97316]/20 text-[#fb923c]" },
  endorsement: { name: "Endorsement", className: "bg-[#3b82f6]/20 text-[#60a5fa]" },
  client: { name: "Client", className: "bg-muted text-muted-foreground" },
  finance: { name: "Finance", className: "bg-muted text-muted-foreground" },
}

/**
 * Color + label map for the ENGINE-classified contract types (the deal-type
 * chip on real inbox rows / deal cards). The backend deal-processing engine's
 * DEAL_TYPE stage emits exactly these six lowercase values; the api-server
 * projects `dealType` and the frontend renders it via AriTag. Without this map
 * the engine types fell through to the gray CLIENT_TAG_TONE fallback — these
 * give them colored pills drawn from the same palette family as the mock
 * ARI_DEAL_LABEL_COLORS above (recording≈music purple, services≈endorsement
 * blue, publishing matches its mock entry, etc.). `tbd` stays muted on purpose:
 * a low-confidence "couldn't classify" outcome shouldn't read as a real tag.
 */
export const ENGINE_DEAL_TYPE_COLORS: Record<string, { name: string; className: string }> = {
  recording: { name: "Recording", className: "bg-[#8b5cf6]/20 text-[#a78bfa]" },
  publishing: { name: "Publishing", className: "bg-[#ec4899]/20 text-[#f472b6]" },
  management: { name: "Management", className: "bg-[#14b8a6]/20 text-[#2dd4bf]" },
  producer: { name: "Producer", className: "bg-[#f97316]/20 text-[#fb923c]" },
  services: { name: "Services", className: "bg-[#3b82f6]/20 text-[#60a5fa]" },
  tbd: { name: "Unclassified", className: "bg-muted text-muted-foreground" },
}

export const DEAL_TYPE_CONFIG: Record<DealType, { label: string; color: string }> = {
  music: { label: "Music", color: "bg-[#55ff0044] text-[#55ff00]" },
  vc: { label: "VC", color: "bg-[#fffb002c] text-[#fffb00]" },
  partnership: { label: "Partnership", color: "bg-[#ff62003d] text-[#ff6200]" },
}

export const PARTICIPANT_ROLES = [
  { value: "legal_counsel", label: "Legal Counsel", description: "Reviews and advises on contract terms" },
  { value: "approver", label: "Approver", description: "Final sign-off authority" },
  { value: "collaborator", label: "Collaborator", description: "Can view and comment on deal" },
  { value: "manager", label: "Manager", description: "Oversees deal progress" },
  { value: "artist_rep", label: "Artist Representative", description: "Represents the artist's interests" },
  { value: "label_rep", label: "Label Representative", description: "Represents the label's interests" },
] as const

export const PERMISSION_LEVELS = [
  { value: "full_access", label: "Full Access", description: "Can edit, comment, and manage participants" },
  { value: "edit", label: "Edit", description: "Can edit documents and add comments" },
  { value: "review", label: "Review Only", description: "Can view and comment only" },
] as const

export const DEFAULT_SORTING = [{ id: "uploadedAt", desc: true }] as const
export const DEFAULT_PAGINATION = { pageSize: 5, pageIndex: 0 }
export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const
